import { Snackbar, Tooltip } from '@shared/ui';
import type { FC } from 'react';
import { useState } from 'react';
import { version } from '../../../../generated/version';

type VersionBadgeProps = {
  className?: string;
  showFullHash?: boolean;
};

const VersionBadge: FC<VersionBadgeProps> = ({ className, showFullHash = false }) => {
  // Read from generated version file (created at build time)
  const gitCommit = version.gitCommit;
  const buildTime = version.buildTime;
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  // Use full hash if requested, otherwise truncate to 7 characters (sufficient for GitHub and git)
  const displayHash = showFullHash
    ? gitCommit
    : gitCommit.length <= 7
      ? gitCommit
      : gitCommit.substring(0, 7);

  // Format build timestamp for display (e.g., "2025-12-23 13:52")
  const formatBuildTime = (isoString: string) => {
    const date = new Date(isoString);
    // Check if date is valid (new Date() doesn't throw, it returns Invalid Date)
    if (Number.isNaN(date.getTime())) {
      return isoString;
    }
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day} ${hours}:${minutes}`;
  };

  const displayBuildTime = formatBuildTime(buildTime);
  const versionInfo = `Version ${displayHash} • Built ${displayBuildTime}`;

  const handleClick = async () => {
    // Copy only the hash for easy git operations
    try {
      await navigator.clipboard.writeText(displayHash);
      setSnackbarOpen(true);
    } catch (_err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = displayHash;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        const success = document.execCommand('copy');
        if (success) {
          setSnackbarOpen(true);
        } else {
          console.error('Failed to copy version: execCommand returned false');
        }
      } catch (fallbackErr) {
        console.error('Failed to copy version:', fallbackErr);
      }
      document.body.removeChild(textArea);
    }
  };

  return (
    <>
      <Tooltip title={`Click to copy version hash: ${displayHash}`} placement="top">
        <span
          className={`text-cement-600 cursor-pointer hover:text-cement-800 transition-colors font-mono text-sm ${className || ''}`}
          style={{ userSelect: 'none' }}
          onClick={handleClick}
        >
          {versionInfo}
        </span>
      </Tooltip>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={2000}
        onClose={() => setSnackbarOpen(false)}
        message={`Copied version hash: ${displayHash}`}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </>
  );
};

export default VersionBadge;
export type { VersionBadgeProps };
